// The v2 board's one line between today's tabs and the table (GRO-276): today's picks, the
// counts, the updates badge and freshness. It replaces the "All accounts" overview, the brief
// card, the update strip and the second "All accounts" heading. "Full brief" and "N updates"
// open today's brief card and notification strip underneath.

import { useState } from 'react';
import type { ReactNode } from 'react';
import type { BriefPayload } from '../api/types';
import { formatCount } from '../lib/format';

const STRIP_PICKS = 3;

export interface StripStats {
  withSignal: number;
  inCommunication: number;
  untouched: number;
  withMeeting: number;
  openOpps: number;
}

// A count with its own label, for a page whose summary is not the board's (the AE view).
export interface StripCount {
  label: string;
  value: number;
  title: string;
  // Drawn instead of the count when the figure is not a count (the oversight view's pipeline $).
  display?: string;
}

function Picks({ brief, onOpenAccount }: { brief: BriefPayload | null; onOpenAccount: (accountId: string) => void }) {
  const picks = brief?.brief_on ? (brief.top_five.length ? brief.top_five : brief.picks) : [];
  if (!picks.length) return <span className="muted">No picks yet</span>;
  return (
    <>
      {picks.slice(0, STRIP_PICKS).map(line => (
        <button key={line.account_id} type="button" className="strippick" onClick={() => onOpenAccount(line.account_id)}>
          {line.account}
        </button>
      ))}
    </>
  );
}

export function BoardStrip({
  brief,
  showBrief,
  stats,
  counts,
  unreadCount,
  asOf,
  onOpenAccount,
  briefPanel,
  notificationStrip,
}: {
  brief: BriefPayload | null;
  showBrief: boolean;
  stats: StripStats;
  // Replaces the board's five counts when given.
  counts?: StripCount[];
  unreadCount: number;
  asOf: string;
  onOpenAccount: (accountId: string) => void;
  briefPanel: ReactNode;
  notificationStrip: ReactNode;
}) {
  const [briefOpen, setBriefOpen] = useState(false);
  const [updatesOpen, setUpdatesOpen] = useState(false);

  return (
    <>
      <div className="fusedstrip">
        {/* Today's picks: each opens its account; "Full brief" opens today's card below. */}
        {showBrief ? (
          <span className="strippicks">
            <span className="striplabel">Today</span>
            <Picks brief={brief} onOpenAccount={onOpenAccount} />
            <button type="button" className="striplink" aria-expanded={briefOpen} onClick={() => setBriefOpen(!briefOpen)}>
              {briefOpen ? 'Hide brief' : 'Full brief'}
            </button>
          </span>
        ) : null}
        <span className="stripsep" />

        {/* The counts, as plain facts. */}
        {counts ? (
          <span className="stripcounts">
            {counts.map(count => (
              <span key={count.label} title={count.title}><b>{count.display ?? formatCount(count.value)}</b> {count.label}</span>
            ))}
          </span>
        ) : (
          <span className="stripcounts">
            <span><b>{formatCount(stats.withSignal)}</b> signal</span>
            <span><b>{formatCount(stats.inCommunication)}</b> talking</span>
            <span><b>{formatCount(stats.untouched)}</b> untouched</span>
            <span><b>{formatCount(stats.withMeeting)}</b> booked</span>
            <span><b>{formatCount(stats.openOpps)}</b> open opps</span>
          </span>
        )}

        <span className="stripright">
          {unreadCount > 0 ? (
            <button type="button" className="stripbadge" aria-expanded={updatesOpen} onClick={() => setUpdatesOpen(!updatesOpen)}>
              {unreadCount} update{unreadCount === 1 ? '' : 's'}
            </button>
          ) : null}
          {asOf ? <span className="muted mono">as of {asOf}</span> : null}
        </span>
      </div>
      {briefOpen ? briefPanel : null}
      {updatesOpen ? notificationStrip : null}
    </>
  );
}
